import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CAT_MAP, formatDate } from "@/lib/categories";
import { CategoryBadge, ArticleCard, ExternalSourceLink } from "@/components/ArticleCard";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { ArticleValueTrend } from "@/components/ValueTrend";
import { PurchaseLinks } from "@/components/PurchaseLinks";
import { ImageCredit } from "@/components/ImageCredit";
import { ChevronLeft, Share2, Check, TrendingUp, Lightbulb } from "lucide-react";
import { Seo, articleLd, breadcrumbLd, clip } from "@/components/Seo";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null); setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((res) => setArticle(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: article.title, url }); return; } catch {} }
    await navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center font-editorial text-2xl text-stone-800">
      <Seo title="Articolo non trovato" noindex />
      Articolo non trovato
    </div>
  );
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-24 animate-pulse"><div className="h-8 rounded bg-panel w-1/3 mb-6" /><div className="h-14 rounded bg-panel mb-4" /><div className="h-80 rounded-xl bg-panel" /></div>;

  const cat = CAT_MAP[article.category];
  const path = `/articolo/${article.slug}`;

  return (
    <article className="fade-up">
      <Seo
        title={article.title}
        description={clip(article.summary)}
        path={path}
        image={article.image_url}
        imageAlt={article.title}
        type="article"
        published={article.published_at || article.created_at}
        modified={article.updated_at || article.published_at}
        jsonLd={[
          articleLd(article, path),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: cat?.name || article.category, path: `/categoria/${article.category}` },
            { name: article.title, path },
          ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 pt-8">
        <Link to={`/categoria/${article.category}`} className="inline-flex items-center gap-1 text-sm font-semibold text-stone-500 hover:text-gold transition-colors mb-6">
          <ChevronLeft size={16} /> {cat?.name}
        </Link>

        <header data-testid="article-header">
          <CategoryBadge slug={article.category} />
          <h1 className="font-editorial font-extrabold text-3xl sm:text-4xl lg:text-[52px] leading-[1.08] tracking-tight text-stone-900 mt-4">
            {article.title}
          </h1>
          <p className="text-lg sm:text-xl text-stone-600 mt-5 leading-relaxed font-light">{article.summary}</p>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-7 py-4 border-y border-line">
            <div className="flex items-center gap-2 text-sm font-meta">
              <span className="text-stone-400 text-xs uppercase tracking-wider">Fonte</span>
              <span className="font-semibold text-stone-800">{article.source_name}</span>
              <span className="text-stone-300">·</span>
              <span className="text-stone-500 text-xs">{formatDate(article.published_at)}</span>
            </div>
            <button onClick={share} data-testid="share-button"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-700 hover:text-gold transition-colors">
              {copied ? <><Check size={15} /> Copiato</> : <><Share2 size={15} /> Condividi</>}
            </button>
          </div>
        </header>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-8">
        <div className="aspect-[16/9] overflow-hidden rounded-3xl bg-panel border-2 border-line">
          <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
        </div>
        <ImageCredit credit={article.image_credit} original={article.image_ref} className="mt-2 text-right" />
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-10">
        {article.key_points?.length > 0 && (
          <aside className={`rounded-2xl bg-panel border-2 border-line border-l-[6px] ${cat?.accent} p-5 mb-10`} data-testid="key-points">
            <h2 className="font-meta text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3">In sintesi</h2>
            <ul className="space-y-2.5">
              {article.key_points.map((k, i) => (
                <li key={i} className="flex gap-2.5 text-stone-700">
                  <span className="font-editorial font-bold" style={{ color: cat?.hex }}>—</span>
                  <span>{k}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {article.price_guidance && (
          <div data-testid="price-guidance" className="mb-8 rounded-2xl bg-gold/[0.07] border-2 border-gold/25 border-l-[6px] border-l-gold p-5">
            <div className="flex items-center gap-1.5 text-[10px] font-meta uppercase tracking-[0.18em] text-gold mb-1.5">
              <TrendingUp size={13} /> Stima AI indicativa
            </div>
            <p className="font-editorial font-bold text-lg text-gold-soft">{article.price_guidance}</p>
            <p className="text-[11px] text-stone-500 mt-2">Stima generata dall'AI a scopo informativo: non è una quotazione di mercato, né una perizia o una consulenza d'investimento.</p>
          </div>
        )}

        <div className="article-body" data-testid="article-body">
          {(article.body || "").split("\n\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {article.value_card && (
          <ArticleValueTrend card={article.value_card} match={article.value_card_match} />
        )}

        {article.practical_advice?.length > 0 && (
          <div data-testid="practical-advice" className={`mt-8 rounded-2xl bg-panel border-2 border-line border-l-[6px] ${cat?.accent} p-5`}>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb size={17} style={{ color: cat?.hex }} />
              <h2 className="font-editorial font-bold text-xl text-stone-900">Consigli pratici</h2>
            </div>
            <ul className="space-y-2.5">
              {article.practical_advice.map((a, i) => (
                <li key={i} className="flex gap-2.5 text-stone-700">
                  <Check size={18} className="mt-0.5 shrink-0" style={{ color: cat?.hex }} />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8">
            {article.tags.map((t) => (
              <span key={t} className="text-[11px] font-meta text-stone-600 bg-panel border border-line px-2.5 py-1 rounded-md">#{t}</span>
            ))}
          </div>
        )}

        <div className="mt-10 p-6 rounded-2xl bg-panel border-2 border-line">
          <p className="text-[10px] font-meta uppercase tracking-[0.18em] text-stone-500 mb-3">Attribuzione</p>
          <p className="text-sm text-stone-600 mb-4">
            Questo è un articolo originale che riferisce i fatti con parole proprie. Per approfondire, consulta la fonte originale:
          </p>
          <ExternalSourceLink name={article.source_name} url={article.source_url} />
        </div>

        {article.purchase_links?.length > 0 && (
          <PurchaseLinks links={article.purchase_links} accentHex={cat?.hex} />
        )}

        <div className="mt-8">
          <DisclaimerBanner />
        </div>
      </div>

      {article.related?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-20">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="font-editorial font-extrabold text-2xl tracking-tight text-stone-900">Articoli correlati</h2>
            <span className="flex-1 h-px bg-line mb-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {article.related.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
          </div>
        </div>
      )}
    </article>
  );
}
